import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
  useLocation,
} from "react-router-dom";
import { I18nProvider, useI18n } from "@/i18n/I18nProvider";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import ScrollToTop from "@/components/ScrollToTop";
import Home from "@/pages/Home";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import Terms from "@/pages/Terms";
import ThankYou from "@/pages/ThankYou";
import NotFound from "@/pages/NotFound";
import "@/App.css";

const SUPPORTED = ["hr", "en"];

function MetaSync() {
  const { lang, t } = useI18n();
  const location = useLocation();
  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = t("meta.title");
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", t("meta.description"));
  }, [lang, t, location.pathname]);
  return null;
}

function LangLayout({ onManageCookies }) {
  const { lang } = useParams();
  const safe = SUPPORTED.includes(lang) ? lang : "hr";
  return (
    <I18nProvider lang={safe}>
      <MetaSync />
      <Header />
      <main id="main">
        <Outlet />
      </main>
      <Footer onManageCookies={onManageCookies} />
    </I18nProvider>
  );
}

function RootRedirect() {
  // Detect saved lang preference or browser language, then send to /hr or /en.
  let stored = null;
  try {
    stored = window.localStorage.getItem("zyravex_lang");
  } catch (_) {}
  const browser = typeof navigator !== "undefined" && navigator.language ? navigator.language.toLowerCase() : "hr";
  const initial = stored && SUPPORTED.includes(stored)
    ? stored
    : browser.startsWith("hr")
      ? "hr"
      : "hr"; // Croatia-first default
  return <Navigate to={`/${initial}`} replace />;
}

export default function App() {
  const [openSignal, setOpenSignal] = useState(0);
  const onManageCookies = () => setOpenSignal((v) => v + 1);

  return (
    <div className="App zx-app">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/thank_you" element={<ThankYou />} />
          <Route path="/:lang" element={<LangLayout onManageCookies={onManageCookies} />}>
            <Route index element={<Home />} />
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="cookies" element={<CookiePolicy />} />
            <Route path="terms" element={<Terms />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="*" element={<Navigate to="/hr" replace />} />
        </Routes>
        {/* Cookie banner is language-aware; render inside a fallback provider so it works on /thank_you too. */}
        <BannerHost openSignal={openSignal} />
      </BrowserRouter>
    </div>
  );
}

function BannerHost({ openSignal }) {
  const location = useLocation();
  // Try to pick language from URL; fallback to hr.
  const seg = location.pathname.split("/").filter(Boolean)[0];
  const lang = SUPPORTED.includes(seg) ? seg : "hr";
  return (
    <I18nProvider lang={lang}>
      <CookieBanner openSignal={openSignal} />
    </I18nProvider>
  );
}
